import React, { ReactNode } from 'react';
import {
  Hidden,
  Table as MuiTable,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@mui/material';
import classNames from 'classnames';
import * as css from './table.module.scss';
import {
  DragDropContext,
  Draggable,
  Droppable,
  DropResult,
} from 'react-beautiful-dnd';
import {
  Table as AlpTable,
  TableHeader as AlpTableHeader,
  TableMain as AlpTableMain,
  TableRow as AlpTableRow,
  TableCellTh as AlpTableCellTh,
} from './alpTable';

interface HeaderColumnDefinition {
  title?: string;
  content?: React.ReactNode;
  alignment?: 'inherit' | 'left' | 'center' | 'right' | 'justify';
}

const NewTable: React.FC<
  React.PropsWithChildren<{
    headers?: HeaderColumnDefinition[];
    rows: React.ReactNode[];
    className?: string;
    activeHeaders?: boolean;
  }>
> = ({ headers, rows, className, activeHeaders }) => {
  const headerCell = (col: HeaderColumnDefinition, i: number) => (
    <AlpTableCellTh key={i} align={col.alignment || 'left'} colIndex={i}>
      {col.content || col.title || ''}
    </AlpTableCellTh>
  );

  return (
    <AlpTable className={className}>
      <AlpTableHeader>
        <AlpTableRow top noActiveHeaders={!activeHeaders}>
          {(headers || []).map(headerCell)}
        </AlpTableRow>
      </AlpTableHeader>
      <AlpTableMain>{rows}</AlpTableMain>
    </AlpTable>
  );
};

export interface TableColumn<T> {
  className?: string;
  title: string;
  alignment?: 'inherit' | 'left' | 'center' | 'right' | 'justify';
  data: (t: T, rowIndex: number) => string | ReactNode;
}

export interface Table<T> {
  caption?: ReactNode;
  dragAndDropTableProps?: DragAndDropTableProps;
  columns: TableColumn<T>[];
  rows: T[];
  rowKey: (row: T) => string;
  className?: string;
}

export interface DragAndDropTableProps {
  dragAndDropId: string;
  onDragEnd: (result: DropResult) => void;
}

export const MobileFriendlyTable = <T extends unknown>({
  caption,
  dragAndDropTableProps,
  columns,
  rows,
  rowKey,
  className,
}: Table<T>) => {
  const headerCell = (header: TableColumn<T>, i: number) => (
    <TableCell
      key={i}
      align={header.alignment || 'left'}
      className={header.className || ''}
    >
      {header.title}
    </TableCell>
  );

  const hiddenHeader = (headerTitle: string) => (
    <Hidden only={['sm', 'md', 'lg', 'xl']}>
      <span aria-hidden="true" className={css.hiddenHeader}>
        {`${headerTitle || ''}: `}
      </span>
    </Hidden>
  );

  const column = (
    col: TableColumn<T>,
    i: number,
    row: T,
    rowIndex: number,
  ): React.ReactNode => (
    <TableCell key={`${rowKey(row)}-col${i}`} className={col.className || ''}>
      {hiddenHeader(col.title)}
      <span>{col.data(row, rowIndex)}</span>
    </TableCell>
  );

  const row = (row: T, rowIndex: number) => {
    const key = rowKey(row);
    return (
      <DragAndDropRow
        rowKey={key}
        key={`dnd-${key}`}
        index={rowIndex}
        useDragAndDrop={!!dragAndDropTableProps}
      >
        {columns.map((col, colIndex) => column(col, colIndex, row, rowIndex))}
      </DragAndDropRow>
    );
  };

  return (
    <DragAndDropContextWrapper
      onDragEnd={dragAndDropTableProps && dragAndDropTableProps.onDragEnd}
    >
      <MuiTable className={classNames(css.mobileFriendlyTable, className)}>
        {caption && <caption className={css.caption}>{caption}</caption>}
        <TableHead>
          <TableRow>{columns.map(headerCell)}</TableRow>
        </TableHead>
        <DragAndDropBody
          dragAndDropId={
            dragAndDropTableProps && dragAndDropTableProps.dragAndDropId
          }
        >
          {rows.map(row)}
        </DragAndDropBody>
      </MuiTable>
    </DragAndDropContextWrapper>
  );
};

const DragAndDropContextWrapper: React.FC<
  React.PropsWithChildren<{
    onDragEnd?: (result: DropResult) => void;
    children: React.ReactNode;
  }>
> = ({ onDragEnd, children }) =>
  (onDragEnd && (
    <DragDropContext onDragEnd={onDragEnd}>{children}</DragDropContext>
  )) || <>{children}</>;

const DragAndDropBody: React.FC<
  React.PropsWithChildren<{
    dragAndDropId?: string;
    children: React.ReactNode;
  }>
> = ({ dragAndDropId, children }) =>
  (dragAndDropId && (
    <Droppable droppableId={dragAndDropId}>
      {(provided) => (
        <TableBody ref={provided.innerRef} {...provided.droppableProps}>
          {children}
          {provided.placeholder}
        </TableBody>
      )}
    </Droppable>
  )) || <TableBody>{children}</TableBody>;

const DragAndDropRow: React.FC<
  React.PropsWithChildren<{
    index: number;
    useDragAndDrop: boolean;
    rowKey: string;
    children: React.ReactNode;
  }>
> = ({ index, useDragAndDrop, rowKey, children }) =>
  (useDragAndDrop && (
    <Draggable draggableId={rowKey} index={index} key={rowKey}>
      {(provided, snapshot) => (
        <TableRow
          ref={provided.innerRef}
          {...provided.draggableProps}
          {...provided.dragHandleProps}
          style={{
            border: snapshot.isDragging ? '1px solid' : undefined,
            backgroundColor: snapshot.isDragging
              ? '#d6eff6' // bg-hovered
              : undefined,
            ...provided.draggableProps.style,
          }}
        >
          {children}
        </TableRow>
      )}
    </Draggable>
  )) || <TableRow key={rowKey}>{children}</TableRow>;

export default NewTable;
